import { useEffect, useState } from 'react';

interface UseTypewriterOptions {
    /** Milliseconds between characters. */
    speed?: number;
    /** Delay before typing starts (e.g. to wait for an entrance animation). */
    startDelay?: number;
    /** Gate typing — stays empty until enabled, then types from the start. */
    enabled?: boolean;
}

interface UseTypewriterResult {
    displayed: string;
    isTyping: boolean;
}

/**
 * Reveals `text` one character at a time. Restarts whenever `text` changes,
 * so swapping the message (prompt → prediction) re-runs the typing animation.
 */
export const useTypewriter = (
    text: string,
    { speed = 30, startDelay = 0, enabled = true }: UseTypewriterOptions = {},
): UseTypewriterResult => {
    const [displayed, setDisplayed] = useState('');
    const [isTyping, setIsTyping] = useState(false);

    useEffect(() => {
        if (!enabled) {
            return;
        }

        // Array.from keeps multi-byte characters (emoji) intact while slicing.
        const characters = Array.from(text);
        setDisplayed('');
        setIsTyping(true);

        let index = 0;
        let intervalId: ReturnType<typeof setInterval>;

        const startId = setTimeout(() => {
            intervalId = setInterval(() => {
                index += 1;
                setDisplayed(characters.slice(0, index).join(''));

                if (index >= characters.length) {
                    clearInterval(intervalId);
                    setIsTyping(false);
                }
            }, speed);
        }, startDelay);

        return () => {
            clearTimeout(startId);
            clearInterval(intervalId);
        };
    }, [text, speed, startDelay, enabled]);

    return { displayed, isTyping };
};
